/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import AnalyticsUtil from '../../../../utils/AnalyticsUtil';

export default class HomeAnalytics {
	static testCode: string;
	static numTasks: number;
	static variation: string;
	ingest: ingestObject;
	analyticsUtils: AnalyticsUtil;

	constructor() {
		this.analyticsUtils = new AnalyticsUtil();
		this.ingest = this.analyticsUtils.ingest;
	}

	/**
	 * [initAnalytics Init Ingest]
	 * @returns {[none]}
	 */
	initAnalytics() {
		this.ingest.initializeDefaultData();
	}

	/**
	 * Post when user clicks all files
	 */
	postRecentAssetsClickAll() {
		const data = {
			setEventSubcategory: 'Recent',
			setEventType: 'click',
			setEventSubtype: 'all-files',
		};

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 *
	 * @param {*} action
	 * @param {*} project
	 */
	postQuickStartModalAction(action: string, project: any) {
		this.initAnalytics();
		const data: dataObject = {
			setEventSubcategory: 'Quick Start',
			setEventType: action,
			setEventSubtype: 'detail-page',
			setContentID: project.id,
			setContentName: project.titleKey,
			setContentAction: project.details.ctaURL,
		};

		/* istanbul ignore next */
		if (window.cchome.util.hasFeature('temp_enable_for_you')) {
			data.setExpCampaignID = '20181129-Onboarding';
			data.setExpVariationID = AnalyticsUtil.userBucket20181129Onboarding;
		}
		// Qverwriting temp_enable_for_you by 20190224_SuggestionsUI

		/* istanbul ignore next */
		if (
			window.cchome.util.hasFeature('20190224_SuggestionsUI')
			&& AnalyticsUtil.userBucket20190224SuggestionsUI !== ''
		) {
			data.setExpCampaignID = '20190224_SuggestionsUI';
			data.setExpVariationID = AnalyticsUtil.userBucket20190224SuggestionsUI;
		}

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 *
	 * @param {*} project
	 */
	postFindAppsClick(project: any) {
		this.initAnalytics();

		const data: dataObject = {
			setEventSubcategory: 'Quick Start',
			setEventType: 'click',
			setEventSubtype: 'task',
			setContentID: project.id,
			setContentName: project.titleKey,
		};

		/* istanbul ignore next */
		if (window.cchome.util.hasFeature('temp_enable_for_you')) {
			data.setExpCampaignID = '20181129-Onboarding';
			data.setExpVariationID = AnalyticsUtil.userBucket20181129Onboarding;
		}

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 *
	 * @param {task} project
	 */
	postQuickStartClick(project: any, url: string, modal = false) {
		this.initAnalytics();
		let data;
		if (modal) {
			data = {
				setEventSubcategory: 'Quick Start',
				setEventType: 'click',
				setEventSubtype: 'task',
				setContentID: project.id,
				setContentName: project.titleKey,
			};
		}
		else {
			data = {
				setEventSubcategory: 'Quick Start',
				setEventType: 'click',
				setEventSubtype: 'task',
				setContentID: project.id,
				setContentName: project.titleKey,
				setContentAction: url,
			};
		}

		/* istanbul ignore next */
		if (window.cchome.util.hasFeature('temp_enable_for_you')) {
			data.setExpCampaignID = '20181129-Onboarding';
			data.setExpVariationID = AnalyticsUtil.userBucket20181129Onboarding;
		}
		// Qverwriting temp_enable_for_you by 20190224_SuggestionsUI

		/* istanbul ignore next */
		if (
			window.cchome.util.hasFeature('20190224_SuggestionsUI')
			&& AnalyticsUtil.userBucket20190224SuggestionsUI !== ''
		) {
			data.setExpCampaignID = '20190224_SuggestionsUI';
			data.setExpVariationID = AnalyticsUtil.userBucket20190224SuggestionsUI;
		}

		this.logAnalytics(data);
		this.sendToIngest();
	}

	postYourOverviewAction(subtype: string) {
		const data: dataObject = {
			setEventSubcategory: 'Your Overview',
			setEventType: 'click',
			setEventSubtype: subtype,
		};

		/* istanbul ignore next */
		if (window.cchome.util.hasFeature('EssentialRouting')) {
			data.setExpCampaignID = '20190221-App-Picker';
			data.setExpVariationID = 'test';
		}

		this.logAnalytics(data);
		this.sendToIngest();
	}

	postYourOverviewRender() {
		const data: dataObject = {
			setEventSubcategory: 'Your Overview',
			setEventType: 'render',
			setEventSubtype: 'message',
		};

		/* istanbul ignore next */
		if (window.cchome.util.hasFeature('EssentialRouting')) {
			data.setExpCampaignID = '20190221-App-Picker';
			data.setExpVariationID = 'test';
		}

		this.logAnalytics(data);
		this.sendToIngest();
	}

	postQuickStartShowMoreAction(action: string) {
		const data: dataObject = {
			setEventSubcategory: 'Quick Start',
			setEventType: action,
			setEventSubtype: 'task-show-more',
		};

		/* istanbul ignore next */
		if (window.cchome.util.hasFeature('temp_enable_for_you')) {
			data.setExpCampaignID = '20181129-Onboarding';
			data.setExpVariationID = AnalyticsUtil.userBucket20181129Onboarding;
		}
		// Qverwriting temp_enable_for_you by 20190224_SuggestionsUI

		/* istanbul ignore next */
		if (
			window.cchome.util.hasFeature('20190224_SuggestionsUI')
			&& AnalyticsUtil.userBucket20190224SuggestionsUI !== ''
		) {
			data.setExpCampaignID = '20190224_SuggestionsUI';
			data.setExpVariationID = AnalyticsUtil.userBucket20190224SuggestionsUI;
		}

		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 *
	 * @param {list of projects} list
	 */
	postQuickStartRender(list: any) {
		this.initAnalytics();
		list.forEach((project) => {
			const data: dataObject = {
				setEventSubcategory: 'Quick Start',
				setEventType: 'render',
				setEventSubtype: 'task',
				setContentID: project.id,
				setContentName: project.titleKey,
				setContentAction: project.details.ctaURL,
				setEventCount: list.length,
			};

			/* istanbul ignore next */
			if (window.cchome.util.hasFeature('temp_enable_for_you')) {
				data.setExpCampaignID = '20181129-Onboarding';
				data.setExpVariationID = AnalyticsUtil.userBucket20181129Onboarding;
			}
			// Qverwriting temp_enable_for_you by 20190224_SuggestionsUI

			/* istanbul ignore next */
			if (
				window.cchome.util.hasFeature('20190224_SuggestionsUI')
				&& AnalyticsUtil.userBucket20190224SuggestionsUI !== ''
			) {
				data.setExpCampaignID = '20190224_SuggestionsUI';
				data.setExpVariationID = AnalyticsUtil.userBucket20190224SuggestionsUI;
			}

			this.logAnalytics(data);
		});

		this.sendToIngest();
	}

	/**
	 * [logAnalytics A utility method to set multiple values at once]
	 * @param  {[object of key/value pairs]} dataObject [events]
	 * @returns {[none]}
	 */
	logAnalytics(dataObject: dataObject) {
		this.ingest.setDataValue('event', 'workflow', 'Home');
		this.analyticsUtils.logAnalytics(dataObject);
	}

	/**
	 * [sendToIngest Post to ingest]
	 * @returns {[none]}
	 */
	sendToIngest() {
		this.analyticsUtils.checkOptin(this.ingest);
	}
}

HomeAnalytics.numTasks = 0;
HomeAnalytics.variation = '';
